<template>
    <v-navigation-drawer v-if="loggedIn" app color="#2e3c56" permanent width="312px">
        <v-img :src="imageAsset" class="ma-5"></v-img>
        <v-list-group v-for="item in routes" :key="`nav_route_${item.name}`" append-icon="" no-action>
            <v-list-item slot="activator" :class="getClass(item)" :to="item.route" dark dense link>
                <v-list-item-icon>
                    <v-icon :color="item.activeRoutes.includes($route.name) ? '#1976d2' : '#FFF'">
                        {{ item.icon }}
                    </v-icon>
                </v-list-item-icon>
                <v-list-item-title class="text-h6">
                    {{ item.label }}
                </v-list-item-title>
            </v-list-item>
            <v-list-item
                v-for="sub in item.sublinks"
                :key="sub.name"
                :class="getClass(sub)"
                :to="sub.route"
                dark
                dense
                link
            >
                <v-list-item-icon>
                    <v-icon :color="sub.activeRoutes.includes($route.name) ? '#1976d2' : '#FFF'">
                        {{ sub.icon }}
                    </v-icon>
                </v-list-item-icon>
                <v-list-item-title class="text-h6">
                    {{ sub.label }}
                </v-list-item-title>
            </v-list-item>
        </v-list-group>
    </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { PermissionsConstants } from '@/permissions'

const imageAsset = require('../assets/company-logo.svg')
export default {
    name: 'NavigationDrawer',
    computed: {
        ...mapState('auth', ['user']),
        ...mapGetters('auth', ['loggedIn']),
    },
    data() {
        return {
            imageAsset,
            routes: [
                {
                    label: 'Users',
                    name: 'user-list',
                    route: '/user',
                    activeRoutes: ['user-list', 'user-view'],
                    icon: 'mdi-account-multiple',
                },
                {
                    label: 'Station',
                    name: 'station-list',
                    route: '/station',
                    activeRoutes: ['station-list', 'station-view', 'station-measurement'],
                    icon: 'mdi-archive-sync-outline',
                },
                {
                    label: 'Perco info',
                    name: 'perco-info',
                    route: '/perco-info',
                    activeRoutes: ['perco-info'],
                    icon: 'mdi-account-multiple',
                },
            ],
        }
    },
    mounted() {
        let routes = []
        if (this.loggedIn) {
            if (this.user.role !== 'customer') {
                routes = this.routes
            }
            if (['super_admin', 'admin'].includes(this.user.role)) {
                routes.push({
                    label: 'Reports',
                    name: 'reports',
                    activeRoutes: [],
                    icon: 'mdi-function-variant',
                    sublinks: [
                        {
                            label: 'Fields',
                            name: 'fields-table',
                            route: '/report/fields',
                            activeRoutes: ['report-fields'],
                            icon: 'mdi-calculator-variant',
                        },
                        {
                            label: 'Stations',
                            name: 'stations-table',
                            route: '/report/stations',
                            activeRoutes: ['report-stations'],
                            icon: 'mdi-calculator-variant',
                        },
                    ],
                })
            }
            if (this.user.role === 'super_admin') {
                routes.push({
                    label: 'Companies',
                    name: 'companies-list',
                    route: '/companies',
                    activeRoutes: ['companies-list', 'company-view'],
                    icon: 'mdi-domain',
                },
                {
                    label: 'Soil types',
                    name: 'soil-type-list',
                    route: '/soil-type',
                    activeRoutes: ['soil-type-list', 'soil-type-view'],
                    icon: 'mdi-grass',
                })
            }
            if (
                ['super_admin'].includes(this.user.role) ||
                (this.user?.user_permissions?.error_logs?.length > 0 &&
                    this.user?.user_permissions?.error_logs?.includes(PermissionsConstants.VIEW))
            ) {
                routes.push({
                    label: 'Station error',
                    name: 'station-error-list',
                    activeRoutes: [],
                    icon: 'mdi-lightbulb-alert-outline',
                    sublinks: [
                        {
                            label: 'Active Errors',
                            name: 'sensor-error',
                            route: '/station-error',
                            activeRoutes: ['sensor-error'],
                            icon: 'mdi-alert-circle-outline',
                        },
                        {
                            label: 'Error History',
                            name: 'station-error-history',
                            route: '/station-error-history',
                            activeRoutes: ['station-error-history'],
                            icon: 'mdi-history',
                        },
                    ],
                })
            }

            if (this.user.role === 'super_admin') {
                routes.push({
                    label: 'Recommendations',
                    name: 'recommendation-table',
                    route: '/recommendation-table',
                    activeRoutes: ['recommendation-table-view'],
                    icon: 'mdi-table',
                })
            }
            if (
                this.user.role === 'super_admin' ||
                (this.user?.user_permissions?.station_messages?.length > 0 &&
                    this.user?.user_permissions?.station_messages?.includes(PermissionsConstants.VIEW))
            ) {
                routes.push({
                    label: 'Station Message',
                    name: 'station-message',
                    route: '/station-message',
                    activeRoutes: ['station-message-view'],
                    icon: 'mdi-table',
                })
            }
            if (this.user.role === 'super_admin') {
                routes.push(
                    {
                        label: 'Cultures',
                        name: 'cultures',
                        activeRoutes: [],
                        icon: 'mdi-barley',
                        sublinks: [
                            {
                                label: 'Cultures',
                                name: 'culture-table',
                                route: '/culture-table',
                                activeRoutes: ['culture-table-view'],
                                icon: 'mdi-barley',
                            },
                            {
                                label: 'Growth phase',
                                name: 'culture-growth-phase',
                                route: '/culture-growth-phase',
                                icon: 'mdi-leaf',
                                activeRoutes: ['culture-growth-phase', 'culture-growth-phase-items'],
                            },
                        ],
                    },
                    {
                        label: 'User Permissions',
                        name: 'user-permissions',
                        route: '/user-permissions',
                        activeRoutes: ['user-permissions-view'],
                        icon: 'mdi-account-alert',
                    },
                    {
                        label: 'Calculations',
                        name: 'calculations',
                        activeRoutes: [],
                        icon: 'mdi-function-variant',
                        sublinks: [
                            {
                                label: 'Recommendations',
                                name: 'calculation-table',
                                route: '/calculation-table',
                                activeRoutes: ['calculation-table-view'],
                                icon: 'mdi-calculator-variant',
                            },
                            {
                                label: 'Dashboard',
                                name: 'dashboard-formulas',
                                route: '/dashboard-formulas',
                                activeRoutes: ['dashboard-formula-view'],
                                icon: 'mdi-view-dashboard-edit',
                            },
                            {
                                label: 'Map',
                                name: 'map-formulas',
                                route: '/map-formulas',
                                activeRoutes: ['map-formula-view'],
                                icon: 'mdi-mapbox',
                            },
                            {
                                label: 'NO3',
                                name: 'no3-formulas',
                                route: '/no3-formulas',
                                activeRoutes: ['no3-formula-view'],
                                icon: 'mdi-all-inclusive',
                            },
                        ],
                    },
                    {
                        label: 'Annual Summary',
                        name: 'annual-summary',
                        route: '/annual-summary',
                        activeRoutes: ['annual-summary'],
                        icon: 'mdi-more',
                    }
                )
            }
            if (
                this.user?.user_permissions?.analytics?.length > 0 &&
                this.user?.user_permissions?.analytics?.includes(PermissionsConstants.VIEW)
            ) {
                routes.push({
                    label: 'Data Analytics',
                    name: 'data-analytics',
                    route: '/data-analytics',
                    activeRoutes: ['data-analytics-view'],
                    icon: 'mdi-chart-line',
                })
            }
            this.routes = routes
        }
    },
    methods: {
        getClass(item) {
            if (item.activeRoutes.includes(this.$route.name)) {
                return 'v-list-item--active'
            }
        },
    },
}
</script>

<style scoped></style>
